import React, { type CSSProperties } from "react"

import ArrowRightAltIcon from "@mui/icons-material/ArrowRightAlt"
import CloseIcon from "@mui/icons-material/Close"
import { Drawer, Grid, LinearProgress } from "@mui/material"
import { Button, Typography } from "@synapse-analytics/synapse-ui"

import styles from "./SideBar.module.scss"

interface IconOptions {
  icon?: "arrow" | "close"
  position?: "left" | "right"
}
interface Props {
  isOpen?: boolean
  onClose?: () => void
  title?: string
  children?: React.ReactElement
  actions?: React.ReactElement[]
  variant?: "default" | "floating"
  closeIcon?: IconOptions
  paperStyle?: CSSProperties
  paperClassName?: string
  isLoading?: boolean
}
/** */
export function SideBar(props: Readonly<Props>): React.ReactElement {
  const {
    children,
    title,
    actions,
    onClose,
    isOpen = false,
    variant,
    closeIcon = {
      icon: "arrow",
      position: "left",
    },
    paperStyle,
    paperClassName,
    isLoading,
  } = props

  const floatingVariantStyle: CSSProperties =
    variant === "floating"
      ? {
          margin: 10,
          height: "calc(100vh - 20px)",
          borderRadius: 16,
        }
      : {}

  return (
    // Drawer should be anchored to the container (settings screen for example) not the body tag
    // TODO:: should use a portal with dynamic anchor to attach it to
    // Meaning we will have to develop our own side bar soon
    <Drawer
      open={isOpen}
      onClose={onClose}
      anchor={"right"}
      // using inline to lessen the level of specificity i will have to write in a css class
      PaperProps={{
        className: paperClassName,
        style: {
          width: "25%",
          minWidth: "350px",
          ...floatingVariantStyle,
          ...paperStyle,
        },
      }}
      slotProps={{ backdrop: { style: { backgroundColor: "rgba(0, 0, 0, 0.2)" } } }}
    >
      <Grid container item className={styles.container}>
        {/* Header */}
        <Grid
          item
          xs={12}
          className={styles.header}
          display={"flex"}
          gap={1.5}
          alignItems={"center"}
          justifyContent={closeIcon.position === "left" ? "flex-start" : "space-between"}
        >
          {closeIcon.position === "left" && onClose && (
            <Grid item>
              <Button onClick={() => onClose?.()} size="small">
                {closeIcon.icon === "close" ? (
                  <CloseIcon sx={{ fontSize: "16px" }} />
                ) : (
                  <ArrowRightAltIcon sx={{ fontSize: "16px" }} />
                )}
              </Button>
            </Grid>
          )}

          <Grid item>
            <Typography variant="h3-bold" textTransform="capitalize">
              {title}
            </Typography>
          </Grid>

          {closeIcon.position === "right" && onClose && (
            <Grid item>
              <Button onClick={() => onClose?.()} size="small">
                {closeIcon.icon === "close" ? (
                  <CloseIcon sx={{ fontSize: "16px" }} />
                ) : (
                  <ArrowRightAltIcon sx={{ fontSize: "16px" }} />
                )}
              </Button>
            </Grid>
          )}
        </Grid>

        {/* Body */}
        <Grid item xs={12} className={styles.body} p={3} position="relative">
          {isLoading && <LinearProgress className={styles.loadingBar} />}
          {children}
        </Grid>

        {/* Actions */}
        {actions && actions.length > 0 && (
          <Grid container item xs={12} justifyContent={"flex-end"} gap={1} className={styles.actions}>
            {actions?.map((action, index) => {
              return (
                <Grid item key={`${index}`}>
                  {action}
                </Grid>
              )
            })}
          </Grid>
        )}
      </Grid>
    </Drawer>
  )
}
